import { useState, useEffect, useCallback, useRef } from "react"
import { useHistory } from "react-router-dom"
import { useLazyQuery, useReactiveVar } from "@apollo/client"
import { DateTime } from "luxon"
import { selectedDayVar, cartIdVar, cartPromoCodeVar } from "cache"
import PropTypes from "prop-types"
import classNames from "classnames"
import toNumber from "lodash/toNumber"
import get from "lodash/get"
import mapValues from "lodash/mapValues"
import map from "lodash/map"
import isNil from "lodash/isNil"
import isEmpty from "lodash/isEmpty"
import { useLoginState } from "components/Login/hooks"
// import { Turnstile, useTurnstile } from "react-turnstile"
import Calendar from "components/shared/Calendar"
// import PartialPageLoader from "components/shared/PartialPageLoader"
import { RATE_AVAILABILITY_COLORS, STEPS } from "shared/constants"
import { formatToDayStartISO, formatDate } from "shared/formatting/datetime"
import { trackEvent } from "shared/analytics"

import ExpandableCard from "./ExpandableCard"
import {
  useRsvpPortalZone,
  useClaimCart,
  useAddPromoToCart,
  useCreateCart
} from "./hooks"
import {
  cartStartTime
  // handleTurnstileError
} from "./helpers"
import {
  PublicParkingAvailability,
  PrivateParkingAvailability
} from "./operations"

import styles from "./SelectDate.module.scss"

// const TURNSTILE_BYPASSED_TOKEN = "bypassed"

const SelectDate = ({
  step,
  setStep,
  onCartStartTimeChange,
  setDetailedRatesAvailability
}) => {
  const history = useHistory()
  const selectedDay = useReactiveVar(selectedDayVar)
  const setSelectedDay = selectedDayVar
  const cartId = useReactiveVar(cartIdVar)
  // const turnstile = useTurnstile()
  const { loading: zoneLoading, data: zoneData } = useRsvpPortalZone()
  const timezone = get(zoneData, "zone.timezone")
  const parkingInventoryId = get(zoneData, "zone.parkingInventoryId")
  const startOfToday = formatToDayStartISO(DateTime.now(), { timezone })
  const [createCartRetryCount, setCreateCartRetryCount] = useState(0)
  const [calendarStartDay, setCalendarStartDay] = useState(null)
  const pendingCalendarStartDay = useRef(null)
  const [isCartReady, setIsCartReady] = useState(false)
  // const [turnstileToken, setTurnstileToken] = useState(null)
  // const [showCalendar, setShowCalendar] = useState(false)
  const [isLoggedIn] = useLoginState()
  const [claimCart, { loading: claimCartLoading }] = useClaimCart()

  // We need to wait for a promo code to be added to the cart before we can proceed
  // if the user is logged in and has a promo code
  const [addPromoToCart, { data: addPromoData, loading: addPromoLoading }] =
    useAddPromoToCart()
  const promoCode = get(addPromoData, "addPromoToCart.cart.promoCode", null)
  const activePromoCode = !isEmpty(promoCode)

  const [createCart, { loading: createCartLoading, error: createCartError }] =
    useCreateCart(timezone)

  // const handleResetTurnstile = () => {
  //   if (turnstileToken) {
  //     setTurnstileToken(null)
  //     turnstile.reset()
  //   }
  // }

  const [
    publicParkingAvailability,
    { data: detailWithRatesData, loading: detailWithRatesLoading }
  ] = useLazyQuery(
    PublicParkingAvailability
    //   {
    //   onCompleted: handleResetTurnstile
    // }
  )

  const [
    privateParkingAvailability,
    { data: privateDetailWithRatesData, loading: privateDetailWithRatesLoading }
  ] = useLazyQuery(
    PrivateParkingAvailability
    // {
    // onCompleted: handleResetTurnstile
    // }
  )

  const cartHelper = useCallback(async () => {
    if (!isEmpty(zoneData)) {
      // Make sure isCartReady is initialized to false before creating a cart
      setIsCartReady(false)
      await createCart()

      setCreateCartRetryCount(0)

      if (isLoggedIn && !isNil(cartPromoCodeVar())) {
        await claimCart()
        await addPromoToCart()
      }

      // we want to make sure that the cart is ready (i.e. promo code is added)
      // before we load the calendar (parking availability query)
      setIsCartReady(true)
    }
  }, [createCart, zoneData, claimCart, addPromoToCart, isLoggedIn])

  useEffect(() => {
    // createCartRetryCount incremented by 1 when there is a network error
    if (
      !isEmpty(createCartError) &&
      createCartError.networkError &&
      createCartRetryCount < 1
    ) {
      cartHelper()
      setCreateCartRetryCount((prev) => prev + 1)
      return
    }

    // After 1st retry, if there is a network error immmediately redirect to home
    if (createCartRetryCount === 1) {
      history.push("/")
      return
    }

    // createCartRetryCount is 0 when in initial call
    cartHelper()
  }, [cartHelper, createCartError, createCartRetryCount, history])

  useEffect(() => {
    if (pendingCalendarStartDay.current !== null) {
      setCalendarStartDay(pendingCalendarStartDay.current)
      pendingCalendarStartDay.current = null
    }
  })

  // This effect will set the detailed parking availability
  // when the selected day changes
  // this data will be used in select rate component
  useEffect(() => {
    const selectedDayWithRates = activePromoCode
      ? get(privateDetailWithRatesData, [
          "privateParkingAvailability",
          selectedDay
        ])
      : get(detailWithRatesData, ["publicParkingAvailability", selectedDay])

    if (selectedDayWithRates) {
      setDetailedRatesAvailability(selectedDayWithRates)
    }
  }, [
    selectedDay,
    activePromoCode,
    detailWithRatesData,
    privateDetailWithRatesData,
    setDetailedRatesAvailability
  ])

  // The calendar returns native Dates built with the browser's tz rules, which can be stale
  // for BC, AB and MB. Only the calendar day is read from them.
  const pickedDay = (date) =>
    DateTime.fromObject({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() })

  // This function will set the selected day
  const handleDateSelected = (e) => {
    const date = formatToDayStartISO(pickedDay(e.value), { timezone })

    // Track date selection
    trackEvent("select_date", {
      selected_date: date
    })

    setSelectedDay(date)
    onCartStartTimeChange(date)
    setStep(STEPS.rate)
  }

  const handleDateChange = () => {
    setSelectedDay(null)
    setStep(STEPS.date)
  }

  // This function will get the detailed parking availability from the selected day
  const handlePageLoading = (e) => {
    // Don't proceed if we don't have a turnstile token
    // if (!turnstileToken) return

    // Shift last day as it seems it is always the start of the next month
    const { lastDay } = e
    lastDay.setDate(lastDay.getDate() - 1)

    const startTime = cartStartTime(startOfToday, timezone)
    // "o" is ordinal day (day of the year)
    const startDay = toNumber(pickedDay(e.firstDay).toFormat("o"))
    const endDay = toNumber(pickedDay(lastDay).toFormat("o"))
    const year = toNumber(pickedDay(e.month).toFormat("y"))

    // If detailWithRatesLoading is loading, we don't want to make another request
    // onPageLoading is called twice on every render.
    const loading = activePromoCode
      ? privateDetailWithRatesLoading
      : detailWithRatesLoading
    if (loading && startDay === calendarStartDay) return

    if (activePromoCode && isNil(cartId)) return

    const parkingAvailabilityQuery = activePromoCode
      ? privateParkingAvailability
      : publicParkingAvailability

    // const cfToken =
    //   turnstileToken === TURNSTILE_BYPASSED_TOKEN ? null : turnstileToken

    const variables = {
      id: parkingInventoryId,
      cartId, // cartId is only needed for private parking
      cartStartTime: startTime,
      startDay,
      endDay,
      year,
      cfToken: null
    }

    parkingAvailabilityQuery({ variables })
    pendingCalendarStartDay.current = startDay
  }

  const ratesAvailability = () => {
    const parkingAvailability = activePromoCode
      ? get(privateDetailWithRatesData, "privateParkingAvailability", {})
      : get(detailWithRatesData, "publicParkingAvailability", {})

    const availabilityByDay = mapValues(parkingAvailability, "status")

    return map(availabilityByDay, (value, date) => {
      let color = ""

      if (value.sold_out) {
        color = RATE_AVAILABILITY_COLORS.soldOut
      } else if (value.unavailable) {
        color = RATE_AVAILABILITY_COLORS.unavailable
      } else if (
        !value.sold_out &&
        !value.unavailable &&
        !value.reservation_not_needed
      ) {
        color = RATE_AVAILABILITY_COLORS.available
      }

      return {
        date,
        highlight: color
      }
    })
  }

  const datesAvailability = () => (
    <div className={styles.availability}>
      <div className={styles.available}>Available</div>
      <div className={styles.unavailable}>Unavailable</div>
      <div className={styles.soldOut}>Sold out</div>
      <div className={styles.noReservation}>No reservation needed</div>
    </div>
  )

  const isLoading =
    zoneLoading ||
    createCartLoading ||
    addPromoLoading ||
    claimCartLoading ||
    !isCartReady
  // || !showCalendar

  // const isCalendarDisabled =
  //   !turnstileToken || detailWithRatesLoading || privateDetailWithRatesLoading

  return (
    <div className="h-100">
      {/* <Turnstile
        className={styles.turnstile}
        sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
        onVerify={(token) => {
          setTurnstileToken(token)
          setShowCalendar(true)
        }}
        onError={(error) =>
          handleTurnstileError(error, () => {
            setTurnstileToken(TURNSTILE_BYPASSED_TOKEN)
            setShowCalendar(true)
          })
        }
      /> */}
      <ExpandableCard
        currentStep={step}
        step={STEPS.date}
        title="2. Date"
        subtitle={formatDate(selectedDay, { timezone })}
        buttonText="Change Date"
        onButtonClick={handleDateChange}
        isLoading={isLoading}
      >
        {!isLoading && (
          <>
            <div
              className={classNames(
                styles.calendarWrapper
                // {
                //   [styles.disabled]: isCalendarDisabled
                // }
              )}
            >
              {/* {isCalendarDisabled && (
                <div className={styles.loaderOverlay}>
                  <PartialPageLoader transparent />
                </div>
              )} */}
              <Calendar
                defaultSelection={null}
                onChange={handleDateSelected}
                onPageLoading={handlePageLoading}
                colors={ratesAvailability()}
                timezone={timezone}
              />
            </div>
            {datesAvailability()}
          </>
        )}
      </ExpandableCard>
    </div>
  )
}

SelectDate.propTypes = {
  step: PropTypes.string,
  setStep: PropTypes.func,
  activePromoCode: PropTypes.bool,
  onCartStartTimeChange: PropTypes.func,
  setDetailedRatesAvailability: PropTypes.func
}

export default SelectDate
